/**
 * productForm.js
 *
 * @version 1.0.0
 */

import { openDrawer } from './drawer.js';
import { updateUI as updateCartUI } from './cartDrawer.js';
import { buildCartItems, cardStockError, initCardOptions, resetCardOption } from './cardOption.js';

/* ----------------------------------------------------------------------- */
/*  DOM elements                                                           */
/* ----------------------------------------------------------------------- */

const SEL = {
	drawer: '[data-cart-drawer]',
	form: '[data-product-form]',
	variantInput: '[data-variant-input]',
	price: '[data-product-price]',
	submitBtn: '.btn',
	qtyInput: '.quantity__input',
	error: '[data-product-error]',
	soldOutBadge: '[data-sold-out-badge]',
	variantItem: '.variants__item',
	giftNote: '[data-gift-note]',
	giftNoteCount: '[data-gift-note-count]',
}

/* ----------------------------------------------------------------------- */
/*  Core helpers                                                           */
/* ----------------------------------------------------------------------- */

/**
 * Shopify AJAX endpoint paths.
 * @readonly
 * @enum
 */
const ENDPOINT = {
	/** Returns the entire cart object as JSON. */
	CART: '/cart.json',
	/** Adds a new line-item to the cart. */
	ADD: '/cart/add.js',
	/** Changes the quantity (or removes) a line-item in the cart. */
	CHANGE: '/cart/change.js',
	/** Updates cart-level attributes, note, or multiple line quantities. */
	UPDATE: '/cart/update.js'
};

/**
 * Format a cent amount into an AUD currency string.
 *
 * @param  {number} cents
 * @return {string}
 */
const formatMoney = (cents) =>
	(cents / 100).toLocaleString('en-AU', {
		style: 'currency',
		currency: 'AUD',
		minimumFractionDigits: 2,
	});

/**
 * Fetch wrapper that always resolves with parsed JSON or throws with
 * a descriptive error.
 *
 * @param url     The endpoint to call.
 * @param opts    Additional fetch options (merged with sensible defaults).
 * @returns       Parsed JSON response.
 * @throws        When the network request fails.
 */
async function fetchJSON(url, opts = {}) {
	const defaults = {
		credentials: 'same-origin',
		headers: {
			'Content-Type': 'application/json',
			'X-Requested-With': 'XMLHttpRequest'
		}
	};

	const res = await fetch(url, { ...defaults, ...opts });
	const data = await res.json().catch(() => ({}));

	if (!res.ok) {
		/* Shopify puts the readable reason ("You can't add more ... to the cart.") in description; message is just "Cart Error". */
		const msg = data?.description || data?.message || `${res.status} ${res.statusText}`;
		throw new Error(msg);
	}
	return data;
};

/**
 * Add lines to the cart in one request: the item, plus any cards that go with it.
 *
 * @param {Array<{id: string, quantity: number, properties?: Object<string,string>}>} items
 */
function addItemsToCart(items) {
	return fetchJSON(ENDPOINT.ADD, {
		method: 'POST',
		body: JSON.stringify({
			items: items,
		}),
	});
}

/**
 * Merge a set of attributes onto the cart. Shopify treats the empty
 * string as a deletion, so pass `{ "My Key": "" }` to remove an entry.
 *
 * @param {Object<string,string>} attributes
 */
const updateCartAttributes = (attributes) =>
	fetchJSON(ENDPOINT.UPDATE, {
		method: 'POST',
		body: JSON.stringify({ attributes })
	});

/**
 * Show a server‑side error in the current product form.
 *
 * @param {HTMLFormElement} form
 * @param {string} message
 */
function showError(form, message) {
	const box = form.querySelector(SEL.error);
	if (!box) return;
	box.textContent = message;
	box.classList.remove('hide');
}

/** Clear any previous error. */
function clearError(form) {
	const box = form.querySelector(SEL.error);
	if (!box) return;
	box.textContent = '';
	box.classList.add('hide');
}

/* ----------------------------------------------------------------------- */
/*  Visibility helper                                                      */
/* ----------------------------------------------------------------------- */

/**
 * Toggle element visibility by syncing both the "hidden" attribute
 * and the ".hide" utility class.
 * @param {HTMLElement} el
 * @param {boolean} show
 */
function setVisibility(el, show) {
	if (!el) return;
	el.classList.toggle('hide', !show);
	el.toggleAttribute('hidden', !show);
}

/* ----------------------------------------------------------------------- */
/*  Pack availability                                                      */
/* ----------------------------------------------------------------------- */

/**
 * Calculate whether a pack variant is available based on single unit inventory.
 *
 * @param {HTMLFormElement} form - The product form element
 * @param {number} packSizeQty - The quantity of single units in the pack
 * @returns {boolean} Whether the pack is available
 */
function isPackAvailable(form, packSizeQty) {
	const singleUnitInventory = Number(form.dataset.singleUnitInventory || 0);
	return singleUnitInventory >= packSizeQty;
}

/**
 * Whether each option's data-available in the form is final (product-form.liquid works it out with
 * the theme's stock rule). Forms without it, such as the cart drawer upsells, are counted here.
 *
 * @param {HTMLFormElement} form
 * @returns {boolean}
 */
function hasAvailabilityInMarkup(form) {
	return form.hasAttribute('data-variant-availability');
}

/**
 * Whether the selected variant can be added to the cart.
 *
 * @param {HTMLFormElement} form
 * @param {HTMLElement}     input – selected variant input
 * @returns {boolean}
 */
function isVariantAvailable(form, input) {
	if (hasAvailabilityInMarkup(form)) {
		return input.dataset.available !== 'false';
	}
	return isPackAvailable(form, Number(input.dataset.packSizeQty || 1));
}

/**
 * The stock each tracked option of a product form draws on, from its data-stock (product-form.liquid).
 * pools: units in stock, by the variant that holds the stock (a drink's single unit, or a merchandise
 * size itself). options: for every tracked option, including ones the picker hides, [pool, units].
 *
 * @param {HTMLFormElement} form
 * @returns {{pools: Object<string, number>, options: Object<string, [string, number]>} | null}
 */
function readStock(form) {
	try {
		return JSON.parse(form.dataset.stock || '');
	} catch (err) {
		return null;
	}
}

/**
 * Check an add against the stock it draws on. Pack sizes have no stock of their own in Shopify, and
 * options can be set to keep selling when out of stock, so Shopify won't always refuse too many. This
 * counts the units being added (quantity times pack size) plus every option of this product already in
 * the cart that draws on the same stock.
 *
 * @param {HTMLFormElement} form
 * @param {HTMLElement}     input – selected variant input
 * @param {number}          quantity – how many of the selected option are being added
 * @returns {Promise<string>} A message for the customer, or '' when there's enough stock.
 */
async function optionStockError(form, input, quantity) {
	const stock = readStock(form);
	const option = stock?.options?.[input.value];
	if (!option) return '';

	const pool = option[0];
	const unitsEach = Number(option[1]) || 1;

	const cart = await fetchJSON(ENDPOINT.CART);
	let unitsInCart = 0;
	(cart.items || []).forEach((item) => {
		const inCart = stock.options[String(item.variant_id)];
		if (inCart && inCart[0] === pool) {
			unitsInCart += (Number(inCart[1]) || 1) * item.quantity;
		}
	});

	const unitsLeft = Number(stock.pools[pool] || 0) - unitsInCart;
	if (unitsEach * quantity <= unitsLeft) return '';

	const canAdd = Math.max(0, Math.floor(unitsLeft / unitsEach));
	if (canAdd === 0) {
		return unitsInCart > 0
			? "Sorry, there's no more stock of this one. What's left is already in your cart."
			: "Sorry, there isn't enough stock left for this option.";
	}
	return `Sorry, we only have enough stock for ${canAdd} more of this option.`;
}

/**
 * Update availability state for all variant options in a form.
 *
 * @param {HTMLFormElement} form
 * @param {number} newInventory - Updated single unit inventory count
 */
export function updatePackAvailability(form, newInventory) {
	/* Product forms: each option's availability is already worked out in the markup */
	if (hasAvailabilityInMarkup(form)) return;

	form.dataset.singleUnitInventory = newInventory;

	form.querySelectorAll(SEL.variantInput).forEach((input) => {
		const packSizeQty = Number(input.dataset.packSizeQty || 1);
		const available = newInventory >= packSizeQty;
		input.dataset.available = available ? 'true' : 'false';
		input.closest(SEL.variantItem)?.classList.toggle('variants__item--sold-out', !available);
	});
}

/**
 * Sync submit-button state & price block after a variant change.
 *
 * @param {HTMLFormElement} form
 * @param {HTMLElement}     input – selected variant input
 */
function updateFormUI(form, input) {
	const wrapper = form.querySelector('[data-price-wrapper]');
	const btn = form.querySelector(SEL.submitBtn);

	const price = Number(input.dataset.price || 0);
	const compare = Number(input.dataset.compare || 0);
	const onSale = compare > price;
	const variantId = input.value;
	const available = isVariantAvailable(form, input);
	const packSize = input.dataset.packSize;

	/* ---------------- Price ---------------- */
	if (wrapper) {
		const regBox = wrapper.querySelector('[data-price-regular]');
		const saleBox = wrapper.querySelector('[data-price-sale]');
		const regVal = wrapper.querySelector('[data-price-regular-value]');
		const saleVal = wrapper.querySelector('[data-price-sale-value]');
		const compVal = wrapper.querySelector('[data-price-compare-value]');

		setVisibility(regBox, !onSale);
		setVisibility(saleBox, onSale);

		if (onSale ? saleVal : regVal) {
			(onSale ? saleVal : regVal).textContent = formatMoney(price);
		}
		if (onSale && compVal) compVal.textContent = formatMoney(compare);

		const packEl = document.querySelector('[data-pack-value]');
		if (packEl && packSize) {
			packEl.textContent = packSize;
		}
	}

	/* ------------ Sold Out Badge ------------ */
	const soldOutBadge = form.querySelector(SEL.soldOutBadge);
	if (soldOutBadge) {
		soldOutBadge.classList.toggle('hide', available)
	}

	/* ------------- Submit CTA ------------- */
	if (btn) {
		const labelSpan = btn.querySelector('span');
		if (labelSpan) {
			labelSpan.textContent = available ? 'Add to Cart' : 'Join Waiting List';
		}

		btn.type = available ? 'submit' : 'button';
		btn.classList.toggle('btn--neutral-darker', !available);

		/* [data-product-submit] powers the delegated "Add to Cart" listener */
		btn.toggleAttribute('data-product-submit', available);

		/* Back‑in‑stock dialog for sold‑out variants */
		if (available) {
			btn.removeAttribute('data-dialog-target');
		} else {
			btn.setAttribute('data-dialog-target', `bis-${variantId}`);
		}
	}
}

/* ----------------------------------------------------------------------- */
/*  Gift note character counter                                            */
/* ----------------------------------------------------------------------- */

/**
 * Wire up a live character counter for any gift-note textarea that has a
 * matching `[data-gift-note-count]` element inside its field wrapper.
 *
 * @param {HTMLElement} root
 */
function initGiftNoteCounters(root = document) {
	root.querySelectorAll(SEL.giftNote).forEach((textarea) => {
		const wrapper = textarea.closest('.field') || textarea.parentElement;
		const counter = wrapper?.querySelector(SEL.giftNoteCount);
		if (!counter) return;

		const update = () => {
			counter.textContent = String(textarea.value.length);
		};
		textarea.addEventListener('input', update);
		update();
	});
}

/* ----------------------------------------------------------------------- */
/*  Event handlers                                                         */
/* ----------------------------------------------------------------------- */

/**
 * Handle changes on any radio/select bearing [data-variant-input].
 *
 * @param {Event} event
 */
function handleVariantChange(event) {
	const form = event.target.closest(SEL.form);
	if (!form) return;

	let input = event.target.closest(SEL.variantInput);
	if (!input && event.target.matches(SEL.qtyInput)) {
		input = form.querySelector(`${SEL.variantInput}:checked`);
	}
	if (!input) return;

	updateFormUI(form, input);
}

/**
 * Handle "Add to Cart" form submissions (standard products only).
 *
 * @param e  Mouse event.
 */
async function handleSubmit(e) {
	const btn = e.target.closest('[data-product-submit]');
	if (!btn) return;

	const form = btn.closest('.card__form') || btn.closest('.product__form');
	if (!form) return;

	e.preventDefault();
	e.stopPropagation();

	/* Guard: prevent double-clicks while a request is in-flight */
	if (btn.dataset.loading === 'true') return;

	clearError(form);

	/* ── Standard form: add a single variant ─────────────────────────── */
	const selected = form.querySelector(`${SEL.variantInput}:checked`);
	const id = selected?.value;
	if (!id) {
		showError(form, 'Please select a variant before adding to cart.');
		return;
	}

	/* The option may be one the browser restored after a reload, so check it's still in stock */
	if (hasAvailabilityInMarkup(form) && !isVariantAvailable(form, selected)) {
		showError(form, 'Sorry, this option is sold out.');
		return;
	}

	const qtyInp = form.querySelector(SEL.qtyInput);
	const qty = Number(qtyInp?.value || 1);

	/* Cards: one per item, each with its own message. Check stock first so the customer gets a clear reason. */
	const stockError = cardStockError(form, qty);
	if (stockError) {
		showError(form, stockError);
		return;
	}

	const items = buildCartItems(form, id, qty);

	/* Gather any gift-note payload for this form. */
	const noteEl = form.querySelector(SEL.giftNote);
	const noteValue = (noteEl?.value || '').trim();
	const noteKey = noteEl?.dataset.giftNoteKey;

	const loader = btn.querySelector('.loader');
	btn.disabled = true;
	btn.dataset.loading = 'true';
	loader?.classList.remove('hide');

	try {
		/* Don't add more than the stock it draws on (Shopify can't always stop it, e.g. for pack sizes) */
		const notEnoughStock = await optionStockError(form, selected, qty).catch((err) => {
			/* If the cart can't be read, let Shopify decide rather than blocking the add */
			console.error('Stock check failed:', err);
			return '';
		});
		if (notEnoughStock) {
			showError(form, notEnoughStock);
			return;
		}

		await addItemsToCart(items);

		/* If the customer supplied a gift message, persist it as a cart
		   attribute so it survives the bundle expand operation. */
		if (noteValue && noteKey) {
			await updateCartAttributes({ [noteKey]: noteValue });
		}

		await updateCartUI();
		openDrawer(document.querySelector(SEL.drawer));

		/* Clear the card option so the next box starts fresh. */
		resetCardOption(form);
	} catch (err) {
		console.error('Add to cart failed:', err);
		showError(form, err.message);
		/* Refresh the cart count and drawer in case Shopify took part of the request before refusing the rest. */
		updateCartUI().catch((refreshErr) => {
			console.error('Cart refresh after a failed add failed:', refreshErr);
		});
	} finally {
		loader?.classList.add('hide');
		btn.disabled = false;
		btn.dataset.loading = 'false';
	}
};

/* ----------------------------------------------------------------------- */
/*  Public API                                                             */
/* ----------------------------------------------------------------------- */

/**
 * Attach product-form behaviour to `document`.
 *
 * @return {() => void}
 */
export function initProductForms() {
	/* Sync any pre-selected variants (e.g. first radio in each form) */
	document.querySelectorAll(`${SEL.variantInput}:checked`)
		.forEach((input) => {
			const form = input.closest(SEL.form);
		});

	/* Recalculate pack availability for all variants on init */
	document.querySelectorAll(SEL.form).forEach((form) => {
		const inventory = Number(form.dataset.singleUnitInventory || 0);
		if (inventory >= 0) {
			updatePackAvailability(form, inventory);
		}
	});

	/* Wire up any gift-note character counters */
	initGiftNoteCounters(document);

	/* Card option: tick box, message boxes and their counters */
	initCardOptions();

	/* Delegated listeners */
	document.addEventListener('click', handleSubmit);
	document.addEventListener('change', handleVariantChange, true);
}

// Initialize the event listeners when the DOM is fully loaded.
document.addEventListener('DOMContentLoaded', initProductForms);
